import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import driverTable from "./driver.table.config";
import orgTable from "./org.table.config";
import TableLayout from "../../layouts/TableLayout/NewTableLayout";
import { useAuth } from "../../hooks/useAuth";
import DriverCreateModal from "./DriverCreateModal";
import OrgCreateModal from "../Organizations/OrgCreateModal";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import Dropdown from "../../shared/Dropdown";
import { DriverComplianceFilterList } from "../../components";
import {
  fetchOrganizations,
  resetOrganizations,
} from "../../redux/reducers/organizations/organizationsSlice";
import { useGetDriversQuery } from "../../redux/services/drivers/driversApi";
import { useFetchAll } from "../../hooks/useFetchAll";

const Drivers = () => {
  const { user } = useAuth();
  const dispatch = useDispatch();

  const [modalVisible, setModalVisible] = useState(false);

  const [type, setType] = useState("Drivers");
  const isDrivers = type === "Drivers";

  const filterTypes = useMemo(
    () =>
      isDrivers
        ? [
            {
              id: "complianceStatus",
              type: "multiple",
              label: "Compliance",
              defaultText: "All",
              content: DriverComplianceFilterList,
            },
          ]
        : [],
    [isDrivers]
  );

  const {
    pagination: { pageIndex, pageSize, setPagination },
    search: { handleGlobalSearch },
    filters: { setFilters },
    queryResult: { data, isLoading, isFetching },
  } = useFetchAll({
    queryHook: useGetDriversQuery,
    defaultPageSize: 20,
    options: { skip: !isDrivers },
  });

  const organizations = useSelector((state) => state.organizations);
  const organizationsStatus = useSelector(
    (state) => state.organizations.status
  );

  // Sort drivers alphabetically by firstName on the frontend
  const tableData = isDrivers
    ? data?.drivers?.slice().sort((a, b) => {
        const nameA = a.firstName?.toLowerCase() || "";
        const nameB = b.firstName?.toLowerCase() || "";
        return nameA.localeCompare(nameB);
      })
    : organizations.data;

  useEffect(() => {
    if (type === "Organizations" && organizationsStatus === "idle") {
      dispatch(fetchOrganizations({ parent: user.parentCompany }));
    }
  }, [organizationsStatus, dispatch, user, type]);

  useEffect(() => {
    return () => {
      dispatch(resetOrganizations());
    };
  }, [dispatch]);

  function handleTypeChange(item) {
    setType(item?.value || item?.label || "Drivers");
  }

  const formTypeOptions = [
    {
      value: "Drivers",
      onClick: handleTypeChange,
      label: "Drivers",
    },
    {
      value: "Organizations",
      onClick: handleTypeChange,
      label: "Organizations",
    },
  ];

  const formTypeTrigger = (
    <div className="text-2xl font-bold flex items-center cursor-pointer ">
      {type}
      <ChevronDownIcon className="h-3 w-3 ml-1" />
    </div>
  );

  const actions = (
    <div>
      <Dropdown
        align="left"
        closeOnClick
        trigger={formTypeTrigger}
        data={formTypeOptions}
      />
    </div>
  );

  const colDefs = isDrivers
    ? driverTable.COLUMN_DEFINITIONS
    : orgTable.COLUMN_DEFINITIONS;
  const pageOptions = isDrivers
    ? driverTable.PAGE_SIZE_OPTIONS
    : orgTable.PAGE_SIZE_OPTIONS;

  const handleDriverCreated = useCallback(() => {
    // RTK Query will automatically refetch due to cache invalidation
  }, []);

  return (
    <>
      <TableLayout
        key={type}
        mainAction={{ onClick: () => setModalVisible(true) }}
        id={isDrivers ? "drivers" : "organizations"}
        title={actions}
        loading={isDrivers ? isLoading || isFetching : organizationsStatus === "loading"}
        columns={colDefs}
        data={tableData || []}
        pagination={isDrivers ? data : undefined}
        pageIndex={pageIndex}
        pageSize={pageSize}
        emptyState={{
          title: isDrivers ? "No drivers found" : "No organizations found",
          description: isDrivers
            ? "Create a driver to get started"
            : "Create an organization to get started",
        }}
        onPaginationChange={setPagination}
        pageSizeOptions={pageOptions}
        fullTextSearch={isDrivers ? handleGlobalSearch : undefined}
        filters={isDrivers ? filterTypes : undefined}
        onFiltersChange={isDrivers ? setFilters : undefined}
      />
      {isDrivers ? (
        <DriverCreateModal
          visible={modalVisible}
          setVisible={setModalVisible}
          onDriverCreated={handleDriverCreated}
        />
      ) : (
        <OrgCreateModal visible={modalVisible} setVisible={setModalVisible} />
      )}
    </>
  );
};

export default Drivers;
